<!--
  AStat — 거대 숫자 KPI
  props:
    label: 상단 카테고리 라벨 (대문자 표시)
    value: 큰 숫자
    unit: 숫자 옆 단위 (명, %, ₩ 등)
    delta: 변동값 — 화살표 + 텍스트로 표시
    deltaKind: ok/err/info — 색상
    deltaSuffix: 화살표 뒤 보조 텍스트 (기본 '전주 대비'). 빈 문자열이면 안 보임.
    deltaIcon: 화살표 표시 여부 (기본 true)
  슬롯 default: 카드 하단에 추가 텍스트/상태 줄
-->
<template>
  <div class="a-stat">
    <div class="a-stat__label">{{ label }}</div>
    <div class="a-stat__row">
      <span class="a-stat__value">{{ value }}</span>
      <span v-if="unit" class="a-stat__unit">{{ unit }}</span>
    </div>
    <!-- delta 영역은 항상 자리를 잡아 카드 간 border 위치 정렬을 보장. -->
    <div class="a-stat__delta" :class="[`is-${deltaKind}`, { 'is-empty': delta == null }]">
      <template v-if="delta != null">
        <span v-if="deltaIcon">{{ deltaKind === 'err' ? '↓' : '↑' }}</span>
        {{ delta }}
        <span v-if="deltaSuffix" class="a-stat__delta-sub">{{ deltaSuffix }}</span>
      </template>
    </div>
    <div v-if="$slots.default" class="a-stat__extras">
      <slot />
    </div>
  </div>
</template>

<script>
export default {
  name: 'AStat',
  props: {
    label: { type: String, required: true },
    value: { type: [String, Number], required: true },
    unit: { type: String, default: '' },
    delta: { type: [String, Number], default: null },
    deltaKind: {
      type: String,
      default: 'ok',
      validator: (v) => ['ok', 'err', 'info'].includes(v),
    },
    deltaSuffix: { type: String, default: '전주 대비' },
    deltaIcon: { type: Boolean, default: true },
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/scss/_pineapple.scss';

.a-stat {
  min-width: 0;
  font-family: $p-font-body;

  &__label {
    font-size: 10px;
    font-weight: 800;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: $p-ink-soft;
    white-space: nowrap;
  }
  &__row {
    display: flex;
    align-items: baseline;
    gap: 6px;
    margin-top: 8px;
  }
  &__value {
    font-size: 32px;
    font-weight: 900;
    letter-spacing: -0.025em;
    line-height: 1;
    font-family: $p-font-mono;
    white-space: nowrap;
    color: $p-ink;
  }
  &__unit {
    font-size: 13px;
    color: $p-ink-mid;
    font-weight: 700;
  }
  &__delta {
    margin-top: 8px;
    font-size: 11px;
    font-weight: 700;
    display: flex;            /* 빈 상태에서도 줄 높이를 가져가도록 block flex */
    align-items: center;
    gap: 4px;
    white-space: nowrap;
    min-height: 14px;         /* delta 없는 카드도 동일 라인 높이 확보 */

    &.is-ok   { color: $p-ok; }
    &.is-err  { color: $p-err; }
    &.is-info { color: $p-info; }
    &.is-empty { visibility: hidden; }  /* 자리만 차지하고 표시는 숨김 */
  }
  &__delta-sub {
    color: $p-ink-soft;
    font-weight: 500;
    margin-left: 2px;
  }
  &__extras {
    margin-top: 10px;
    padding-top: 10px;
    border-top: 1px solid $p-line-soft;
    display: flex;
    flex-direction: column;
    gap: 4px;
    font-size: 11px;
    color: $p-ink-soft;
    line-height: 1.4;
  }
}
</style>
